/**
 * Deterministic life-period theme engine.
 *
 * A third birth-time discriminator alongside the KP event engine and the trait engine. The
 * user describes the dominant theme(s) of broad life periods (5-year buckets), and for each
 * period+theme the engine checks whether the candidate's running Vimshottari dasha during
 * that period signifies the houses that theme activates. Because the dasha timeline shifts
 * with birth time, two candidate times produce different period themes — so vague,
 * easy-to-recall "what was life like then" answers still narrow the rising time. Fully
 * deterministic: no AI, no free text.
 *
 * Themes map to bhava (house) sets the way classical timing does; the score reuses the KP
 * signed-significator machinery (net house strength + dasha-chain significator scoring).
 */

import { local_to_jd } from "../astro/chart";
import { init_dasha_jd } from "../astro/dasha";
import { net_significator_strength } from "../astro/kp";
import type { RawChartData } from "../astro/models";
import { weighted_mean } from "./event_model";
import { dasha_net_significator_score } from "./kp_scoring";
import type { PlainDateTime } from "./datetime";
import { diffSeconds, plainDateTime } from "./datetime";
import { pythonRound } from "./pyutil";

// Each theme: the houses it activates (positive) and the houses that contradict it. Houses
// are whole-sign bhavas 1-12. Chosen to match classical period significations.
export const LIFE_PERIOD_THEMES: Record<string, Record<string, any>> = {
  education:        { label: "Education / studies / exams",          houses: [4, 9, 11], contra: [3, 8, 12] },
  career_growth:    { label: "Career growth / promotion / success",  houses: [2, 6, 10, 11], contra: [5, 8, 12] },
  career_trouble:   { label: "Job loss / career setback / failure",  houses: [1, 5, 8, 12], contra: [2, 10, 11] },
  marriage:         { label: "Marriage / serious relationship",      houses: [2, 7, 11], contra: [1, 6, 10] },
  relationship_end: { label: "Breakup / divorce / separation",       houses: [1, 6, 12], contra: [2, 7, 11] },
  children:         { label: "Childbirth / parenthood",              houses: [2, 5, 11], contra: [1, 4, 10] },
  health:           { label: "Major illness / mental health strain", houses: [6, 8, 12], contra: [1, 5, 11] },
  injury:           { label: "Accident / injury / surgery",          houses: [6, 8, 12], contra: [1, 5, 11] },
  finance_gain:     { label: "Financial gain / property / wealth",   houses: [2, 4, 11], contra: [6, 8, 12] },
  finance_loss:     { label: "Financial loss / debt / theft",        houses: [6, 8, 12], contra: [2, 5, 11] },
  relocation:       { label: "Relocation / foreign move / travel",   houses: [3, 4, 9, 12], contra: [1, 11] },
  bereavement:      { label: "Family bereavement / grief",           houses: [2, 7, 8], contra: [1, 5, 11] },
  spiritual:        { label: "Spiritual / inner turning point",      houses: [5, 9, 12], contra: [2, 6, 10] },
};

const _BUCKET_YEARS = 5;

/**
 * Five-year buckets from the birth year up to (and including) the current year.
 * Returns (start_year, end_year, key) where key is 'YYYY-YYYY'. The first bucket starts
 * a couple of years after birth so it covers describable childhood, not infancy.
 */
export function life_period_buckets(birth_year: number, current_year: number): Array<[number, number, string]> {
  const buckets: Array<[number, number, string]> = [];
  let start = birth_year + 2;
  while (start <= current_year) {
    const end = Math.min(start + _BUCKET_YEARS, current_year);
    buckets.push([start, end, `${start}-${end}`]);
    start += _BUCKET_YEARS;
  }
  return buckets;
}

/** Python int() over a string: strict integer literal or ValueError. */
function _py_int(text: string): number {
  const s = String(text).trim();
  if (!/^[+-]?\d+$/.test(s)) {
    throw new Error(`invalid literal for int() with base 10: '${text}'`);
  }
  return Number(s);
}

export function _parse_period_key(key: any): [number, number] | null {
  let start: number;
  let end: number;
  try {
    const text = String(key);
    const dash = text.indexOf("-");
    if (dash < 0) {
      // Python str.split("-", 1) with no separator yields one part; unpacking raises ValueError.
      return null;
    }
    const start_s = text.slice(0, dash);
    const end_s = text.slice(dash + 1);
    start = _py_int(start_s);
    end = _py_int(end_s);
  } catch {
    return null;
  }
  if (end < start) {
    [start, end] = [end, start];
  }
  return [start, end];
}

function _is_plain_object(value: any): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Keep only period keys that parse to a year range, mapped to a list of known theme
 * keys. A legacy single-string value is coerced to a one-item list.
 */
export function normalize_life_period_answers(answers: any): Record<string, string[]> {
  if (!_is_plain_object(answers)) {
    return {};
  }
  const clean: Record<string, string[]> = {};
  for (const [key, raw] of Object.entries(answers)) {
    if (_parse_period_key(key) === null) {
      continue;
    }
    let value: any = raw;
    if (typeof value === "string") {
      value = [value];
    }
    if (!Array.isArray(value)) {
      continue;
    }
    const seen = new Set<string>();
    const themes: string[] = [];
    for (const t of value) {
      if (typeof t === "string" && t in LIFE_PERIOD_THEMES && !seen.has(t)) {
        seen.add(t);
        themes.push(t);
      }
    }
    if (themes.length) {
      clean[key] = themes;
    }
  }
  return clean;
}

/**
 * Score how well the candidate's dasha timeline matches the reported life-period
 * themes. Returns (0-100 or None if no usable answers, detail).
 *
 * For each period+theme: take the houses the theme activates (minus its contra houses),
 * compute the chart's signed significator strength, and score whether the dasha chain
 * running at the period's midpoint is ruled by those significators. The maha lord
 * dominates a 5-year period, so the midpoint maha/antar capture its theme.
 */
export function score_life_periods_for_candidate(
  natal_chart: RawChartData,
  birth_dt: PlainDateTime,
  answers: any,
  timezone_offset: number,
): [number | null, Record<string, any>] {
  const clean = normalize_life_period_answers(answers);
  if (!Object.keys(clean).length) {
    return [null, {}];
  }

  const dasha_init = init_dasha_jd(natal_chart, local_to_jd(birth_dt, timezone_offset));
  const components: Array<[number, number]> = [];
  const per_theme: Record<string, any> = {};
  for (const [period_key, themes] of Object.entries(clean)) {
    const years = _parse_period_key(period_key);
    if (years === null) {
      continue;
    }
    const [start_year, end_year] = years;
    const midpoint = plainDateTime(Math.floor((start_year + end_year) / 2), 7, 1);
    if (diffSeconds(midpoint, birth_dt) <= 0) {
      continue; // period entirely before birth — skip (no valid dasha)
    }
    for (const theme of themes) {
      const spec = LIFE_PERIOD_THEMES[theme];
      const net = net_significator_strength(natal_chart, spec["houses"], spec["contra"] ?? null);
      // Cap at antar: a 5-year period's theme is set by the maha/antar lords, which
      // barely shift across a single day. Keeping it coarse makes this a clean
      // rising-sign signal (no second-level wobble in the RS lane).
      const [score] = dasha_net_significator_score(
        midpoint, birth_dt, dasha_init, timezone_offset, net, "maha", "antar",
      );
      if (score === null) {
        continue;
      }
      components.push([score, 1.0]);
      per_theme[`${period_key}:${theme}`] = pythonRound(score, 1);
    }
  }

  if (!components.length) {
    return [null, per_theme];
  }
  return [weighted_mean(components), {
    answered_periods: Object.keys(clean).length,
    scored_themes: Object.keys(per_theme).length,
    per_theme: per_theme,
  }];
}
